import { ClientProcessComment } from "@avenueops/shared-types";
import { DocumentIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { CloudArrowUpIcon } from "@heroicons/react/24/outline";
import { FileState } from "@prisma/client";
import ModalBlankCenterPanel from "components/generic-building-blocks/ModalBlankCenterPanel";
import ModalShadow from "components/generic-building-blocks/ModalShadow";
import _ from "lodash";
import { useRouter } from "next/router";
import { RefObject, useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { getFileSizeString } from "utils/fileUtils";
import { trpc } from "utils/trpc";

const imageFileTypes = new Set(["gif", "png", "jpg", "gif", "image/gif", "image/png", "image/jpg", "image/jpeg"]);

interface Props {
  fileUpload: ClientProcessComment["attachments"][0];
}

export default function FileUpload({ fileUpload }: Props): JSX.Element | null {
  const uploadRef = useRef<HTMLButtonElement>(null);
  const router = useRouter();
  const { attachment } = router.query;
  const [expandedFileVisible, setExpandedFileVisible] = useState<boolean>(false);

  useEffect(() => {
    if (attachment && typeof attachment === "string" && uploadRef.current) {
      uploadRef.current.focus();
    }
  }, [attachment, uploadRef.current]);

  const { data: fileUrl, isLoading } = trpc.processComments.getAttachmentUrl.useQuery({ attachmentId: fileUpload.id });

  if (!isLoading && !fileUrl) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex w-full mr-1 mt-1 ">
        <div className="border border-cool-gray-300 p-2 group-hover:bg-cool-gray-50 group-hover:shadow-md max-h-96 max-w-full">
          <Skeleton height={100} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full mr-1 mt-1">
      {fileUpload.state === FileState.FAILED ? (
        <div className={`flex flex-row p-2 px-3 bg-gray-100 border-gray-600 text-gray-600 border rounded-md justify-center`}>
          <div className="text-xs flex flex-row">
            <CloudArrowUpIcon className="inline w-4 mr-1" />
            {_.capitalize(fileUpload.origin)} file upload failed! Please try again.
          </div>
        </div>
      ) : (
        <>
          <ModalShadow setModalVisible={setExpandedFileVisible} modalVisible={expandedFileVisible}>
            <ModalBlankCenterPanel>
              <div className="inline-block">
                <img src={fileUrl} alt={`${fileUpload.name}`} />
              </div>
            </ModalBlankCenterPanel>
          </ModalShadow>
          {imageFileTypes.has(fileUpload.filetype) ? (
            <button
              className="group relative h-fit w-[150px] rounded-sm focus:ring focus:ring-primary-regular focus:outline-none border border-cool-gray-300 p-2 hover:bg-cool-gray-50"
              ref={uploadRef}
              type="button"
              onClick={() => {
                setExpandedFileVisible(true);
              }}
            >
              <img className="rounded-sm hover:cursor-zoom-in group-hover:shadow-md" src={fileUrl} alt={`${fileUpload.name}`} />
              <a href={fileUrl} download={fileUpload.name}>
                <ArrowDownTrayIcon className="text-gray-500 border border-cool-gray-300 bg-white invisible group-hover:visible absolute top-2 right-2 h-5 w-5 z-10" />
              </a>
            </button>
          ) : (
            <button
              ref={uploadRef}
              className="group relative flex items-center h-[150px] w-[150px] hover:cursor-pointer border border-cool-gray-300 hover:bg-cool-gray-50 hover:shadow-md rounded-sm focus:ring focus:ring-primary-regular focus:outline-none"
            >
              <a href={fileUrl} download={fileUpload.name}>
                <DocumentIcon className="w-5 h-5 inline-block text-gray-500" />
                <span className="flex flex-col ml-0.5 text-gray-500">
                  {fileUpload.name}
                  {(fileUpload.filesize ?? false) && <span className="text-sm">{getFileSizeString(fileUpload.filesize ?? 0)}</span>}
                </span>

                <ArrowDownTrayIcon className="text-gray-500 invisible group-hover:visible absolute top-2 right-2 h-5 w-5 z-10" />
              </a>
            </button>
          )}
        </>
      )}
    </div>
  );
}
